import { AbortEndpointRequestError } from '@iconik-io/iconik-api/src/client/response';

import { WholeFileBody } from '../../client/whole_file_body';
import {
  AbortUploadError,
  FileSizeError,
  InvalidFileObject,
  MultipartUploadChunkError,
} from '../../errors';
import {
  UPLOADER_CHUNK_STATUS_COMPLETED,
  UPLOADER_CHUNK_STATUS_FAILED,
  UPLOADER_CHUNK_STATUS_UPLOADING,
  UPLOADER_STATUS_COMPLETING,
} from '../../state';
import type { UploadEntity } from '../../types';
import { unwrapHeaderValue } from '../../utils/unwrap_header_value';
import { MultipartUploader } from '../multipart_uploader';
import { handleGcsApiError } from './errors';

const FILE_SIZE_LIMIT = 5_000_000_000_000;

const SINGLE_CHUNK_INDEX = 0;

type GcsWholeFileMeta = {
  uploadUrl: string;
};

/**
 * Uploads the whole file to GCS as one object: a POST that hands back a
 * location, then a PUT of the entire body to it.
 *
 * A separate class rather than a branch inside GcsUploader, which splits the
 * file into one GCS object per chunk and composes them server-side. Proxies
 * cannot do that: the API exposes no compose endpoint for them, and their
 * cleanup body cannot even describe the parts a compose would have produced.
 */
export class GcsWholeFileUploader extends MultipartUploader<
  GcsWholeFileMeta,
  never
> {
  handleUploadEntityCreated = ({ entity }: { entity: UploadEntity }) => {
    if (!entity.upload_url) {
      throw new InvalidFileObject(entity, 'Empty upload URL');
    }
    this.updateState({ meta: { uploadUrl: entity.upload_url } });
  };

  init(): void {
    this.on('uploadEntityCreated', this.handleUploadEntityCreated);
    super.init();
  }

  protected validate(): boolean {
    if (this.state.fileInfo.size > FILE_SIZE_LIMIT) {
      throw new FileSizeError(FILE_SIZE_LIMIT);
    }
    return true;
  }

  checkIfUploadExpired(_assetId: string, _entityId: string): Promise<boolean> {
    return Promise.resolve(false);
  }

  /** One request carries everything, so there is exactly one chunk. */
  protected getChunkSize(): number {
    return this.state.fileInfo.size;
  }

  /**
   * Nothing to open: the body is handed to the http client as it is, so that
   * the file never has to be held in memory.
   */
  protected openSource(): void {}

  protected async runUpload(): Promise<void> {
    const body = new WholeFileBody(this.state.fileInfo);

    // A retry sends the file from the start again, so whatever the previous
    // attempt reported has to be given back or the progress runs backwards.
    this.updateChunks([
      {
        index: SINGLE_CHUNK_INDEX,
        status: UPLOADER_CHUNK_STATUS_UPLOADING,
        uploaded: 0,
      },
    ]);

    try {
      const startResponse = await this.httpClient
        .request(
          {
            method: 'post',
            url: this.unwrapMeta().uploadUrl,
            headers: { 'x-goog-resumable': 'start' },
          },
          {
            abortSignal: this.abortList.newSignal(),
            retryOptions: { method: ['post'] },
          },
        )
        .catch(handleGcsApiError);

      await this.httpClient
        .request(
          {
            method: 'put',
            url: unwrapHeaderValue(startResponse, 'location'),
            body,
          },
          {
            onUploadProgress: (event) =>
              this.uploadChunkProgress(SINGLE_CHUNK_INDEX, event),
            abortSignal: this.abortList.newSignal(),
          },
        )
        .catch(handleGcsApiError);
    } catch (error) {
      if (
        error instanceof AbortUploadError ||
        error instanceof AbortEndpointRequestError
      ) {
        throw error;
      }
      this.updateChunks([
        { index: SINGLE_CHUNK_INDEX, status: UPLOADER_CHUNK_STATUS_FAILED },
      ]);
      throw new MultipartUploadChunkError(
        this.unwrapChunk(SINGLE_CHUNK_INDEX),
        error instanceof Error ? error : undefined,
      );
    }

    this.updateChunks([
      { index: SINGLE_CHUNK_INDEX, status: UPLOADER_CHUNK_STATUS_COMPLETED },
    ]);
    this.updateState({ status: UPLOADER_STATUS_COMPLETING });
  }

  /** Nothing is left behind on the storage: the PUT either landed or it did not. */
  protected async runCancel(): Promise<void> {}
}
